import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ChevronLeft,
    Truck,
    Wrench,
    Hammer,
    Monitor,
    Smartphone,
    TrendingUp,
    AlertCircle,
    DollarSign,
    Calendar,
    Upload,
    Package,
    ShieldCheck,
    Box,
    Building2,
    User,
    CheckCircle2,
    Info,
    Trash2,
    Plus,
    Anchor,
    Armchair,
    LayoutGrid,
    Signature,
    Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/utils';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../utils/api';
import { AssetType, AssetStatus, AssetCondition, AssetCustomField } from '../../types/asset';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const ASSET_TYPES = [
    { id: 'Vehicle', label: 'Vehicles', icon: <Truck size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'Plant & Equipment', label: 'Plant & Equipment', icon: <Wrench size={20} />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { id: 'Tools', label: 'Tools', icon: <Hammer size={20} />, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: 'Lifting/Slings', label: 'Lifting/Slings', icon: <Anchor size={20} />, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: 'IT Equipment', label: 'IT Equipment', icon: <Monitor size={20} />, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { id: 'Mobile Devices', label: 'Mobile Devices', icon: <Smartphone size={20} />, color: 'text-pink-600', bg: 'bg-pink-50' },
    { id: 'Furniture', label: 'Furniture', icon: <Armchair size={20} />, color: 'text-teal-600', bg: 'bg-teal-50' },
];

const STATUS_OPTIONS = [
    'Available',
    'In Triage',
    'Assigned',
    'In Maintenance',
    'Needs Repair',
    'Damaged',
    'Archived',
    'Disposed'
];

const CONDITION_OPTIONS = [
    'New',
    'Excellent',
    'Good',
    'Fair',
    'Poor',
    'Damaged'
];

const CreateAsset: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [organizations, setOrganizations] = useState<any[]>([]);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        tag: '',
        type: '' as AssetType | '',
        brand: '',
        model: '',
        serialNumber: '',
        description: '',
        purchaseDate: '',
        registrationDate: '',
        purchasePrice: '',
        warrantyExpiryDate: '',
        status: 'Available' as AssetStatus,
        condition: 'Excellent' as AssetCondition,
        location: '',
        assignmentType: 'unassigned' as 'unassigned' | 'user' | 'department',
        assignedToUserId: '',
        assignedToDepartmentId: '',
        assignmentNotes: '',
        inspectionCadenceNumber: '0',
        inspectionCadenceUnit: 'days' as 'days' | 'weeks' | 'months',
        addToElectricalRegister: false,
        electricalCadenceNumber: '',
        electricalCadenceUnit: 'days' as 'days' | 'weeks' | 'months',
        addToLiftingRegister: false,
        liftingCadenceNumber: '',
        liftingCadenceUnit: 'days' as 'days' | 'weeks' | 'months',
        image: null as File | null,
        organizationId: '',
    });

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    // Signature State
    const [signatureType, setSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [signatureData, setSignatureData] = useState('');
    const [typedSignature, setTypedSignature] = useState('');
    const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    // Custom Fields State
    const [customFields, setCustomFields] = useState<AssetCustomField[]>([]);
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

    useEffect(() => {
        const fetchCustomFields = async () => {
            if (!formData.type) {
                setCustomFields([]);
                setCustomFieldValues({});
                return;
            }
            try {
                const response = await api.get(`/api/asset-custom-fields/type/${encodeURIComponent(formData.type)}`);
                const activeFields = Array.isArray(response) ? response.filter((f: any) => f.isActive) : [];
                // Sort by listOrder
                activeFields.sort((a: any, b: any) => a.listOrder - b.listOrder);
                setCustomFields(activeFields);

                // Initialize values
                const initialValues: Record<string, any> = {};
                activeFields.forEach((field: any) => {
                    initialValues[field.fieldName] = field.fieldType === 'boolean' ? 'No' : '';
                });
                setCustomFieldValues(initialValues);
            } catch (err) {
                console.error('Error fetching custom fields:', err);
            }
        };
        fetchCustomFields();
    }, [formData.type]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersRes, deptsRes, orgsRes, electricalRes, liftingRes] = await Promise.all([
                    api.get('/api/users?all_users=true').catch(() => ({ users: [] })),
                    api.get('/api/departments').catch(() => ({ departments: [] })),
                    api.get('/api/organization').catch(() => ({ organizations: [] })),
                    api.get('/api/assets/settings/electrical').catch(() => null),
                    api.get('/api/assets/settings/lifting').catch(() => null)
                ]);
                setUsers(usersRes.users || []);
                setDepartments(deptsRes.departments || []);
                setOrganizations(orgsRes.organizations || []);
                
                if (electricalRes) {
                    setFormData(prev => ({
                        ...prev,
                        electricalCadenceNumber: electricalRes.defaultElectricalCadenceNumber,
                        electricalCadenceUnit: electricalRes.defaultElectricalCadenceUnit
                    }));
                }
                
                if (liftingRes) {
                    setFormData(prev => ({
                        ...prev,
                        liftingCadenceNumber: liftingRes.defaultLiftingCadenceNumber,
                        liftingCadenceUnit: liftingRes.defaultLiftingCadenceUnit
                    }));
                }
            } catch (err) {
                console.error('Error fetching dependency data:', err);
            }
        };
        fetchData();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image: null }));
        setImagePreview(null);
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
    };

    const stopDrawing = () => setIsDrawing(false);

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        setIsDrawing(true);
        ctx.beginPath();
        ctx.moveTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        ctx.lineTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
        ctx.stroke();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handleConfirmSignature = () => {
        if (signatureType === 'Type') {
            if (!typedSignature.trim()) {
                toast.error('Please enter your name');
                return;
            }
            setSignatureData(typedSignature.trim());
        } else {
            if (canvasRef.current) {
                const dataUrl = canvasRef.current.toDataURL();
                setSignatureData(dataUrl);
            }
        }
        setIsSignatureDialogOpen(false);
    };

    if (!ability.can(ACTIONS.CREATE, RESOURCES.ASSET)) {
        return <AccessRestricted />;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validateDate = (dateStr: string) => {
            if (!dateStr) return false;
            const year = dateStr.split('-')[0];
            return year.length === 4;
        };

        const newErrors: Record<string, boolean> = {};
        if (!formData.name) newErrors.name = true;
        if (!formData.tag) newErrors.tag = true;
        if (!formData.type) newErrors.type = true;
        
        if (!formData.purchaseDate || !validateDate(formData.purchaseDate)) {
            newErrors.purchaseDate = true;
            toast.error('Invalid Purchase Date. Year must be 4 digits.');
        }
        
        if (formData.registrationDate && !validateDate(formData.registrationDate)) {
            newErrors.registrationDate = true;
            toast.error('Invalid Registration Date. Year must be 4 digits.');
        }
        
        if (!formData.warrantyExpiryDate || !validateDate(formData.warrantyExpiryDate)) {
            newErrors.warrantyExpiryDate = true;
            toast.error('Invalid Warranty Expiry Date. Year must be 4 digits.');
        }

        if (ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE)) {
            const price = parseFloat(formData.purchasePrice);
            if (!formData.purchasePrice || isNaN(price) || price <= 0) {
                newErrors.purchasePrice = true;
            }
        }

        // Validate assignment signature if an assignment is made
        if (formData.assignmentType !== 'unassigned') {
            if (formData.assignmentType === 'user' && !formData.assignedToUserId) {
                newErrors.assignmentUser = true;
            }
            if (formData.assignmentType === 'department' && !formData.assignedToDepartmentId) {
                newErrors.assignmentDepartment = true;
            }
            if (!signatureData) {
                toast.error('Please provide a digital signature for the assignment');
                return;
            }
        }

        // Validate custom fields
        customFields.forEach(field => {
            if (field.isRequired && !customFieldValues[field.fieldName]) {
                newErrors[`custom_${field.fieldName}`] = true;
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fill in all required fields correctly');
            return;
        }

        try {
            setLoading(true);

            let imageUrl = '';
            if (formData.image) {
                const imgFormData = new FormData();
                imgFormData.append('image', formData.image);
                const uploadRes = await api.post('/api/assets/upload', imgFormData, { skipErrorToast: true });
                imageUrl = uploadRes.url;
            }

            const payload = {
                ...formData,
                image: imageUrl,
                purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
                registrationDate: formData.registrationDate || undefined,
                electricalCadenceNumber: formData.electricalCadenceNumber ? parseInt(formData.electricalCadenceNumber) : null,
                electricalCadenceUnit: formData.electricalCadenceUnit,
                liftingCadenceNumber: formData.liftingCadenceNumber ? parseInt(formData.liftingCadenceNumber) : null,
                liftingCadenceUnit: formData.liftingCadenceUnit,
                assignedToUserId: formData.assignmentType === 'user' ? formData.assignedToUserId : undefined,
                assignedToDepartmentId: formData.assignmentType === 'department' ? formData.assignedToDepartmentId : undefined,
                customFields: customFieldValues,
                signatureType: formData.assignmentType !== 'unassigned' ? signatureType : undefined,
                signatureData: formData.assignmentType !== 'unassigned' ? signatureData : undefined,
                organizationId: formData.organizationId ? parseInt(formData.organizationId) : undefined,
                addToElectricalRegister: formData.addToElectricalRegister,
                addToLiftingRegister: formData.addToLiftingRegister,
            };

            await api.post('/api/assets', payload, { skipErrorToast: true });
            toast.success('Asset created successfully');
            navigate('/assets');
        } catch (err: any) {
            toast.error(err.message || 'Failed to create asset');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/assets')}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create New Asset</h1>
                    <p className="text-gray-500 font-medium text-sm">Add a new asset to the company inventory</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form Area */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Basic Information */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <Info size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Basic Information</h3>
                                <p className="text-gray-500 text-sm font-medium">Essential details about the asset</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset Name *</label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter asset name"
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.name && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset ID/Tag *</label>
                                <Input
                                    name="tag"
                                    value={formData.tag}
                                    onChange={handleInputChange}
                                    placeholder="e.g., VEH-001, IT-001"
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.tag && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Organisation</label>
                            <Select value={formData.organizationId} onValueChange={(val) => handleSelectChange('organizationId', val)}>
                                <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                    <SelectValue placeholder="Select Organisation" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-gray-100">
                                    {organizations.map(org => (
                                        <SelectItem key={org.id} value={org.id.toString()} className="font-bold py-3">{org.companyName}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset Type *</label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {ASSET_TYPES.map(type => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, type: type.id as AssetType }));
                                            setErrors(prev => ({ ...prev, type: false }));
                                        }}
                                        className={cn(
                                            "flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left",
                                            formData.type === type.id
                                                ? "border-blue-600 bg-blue-50/30 text-blue-700 shadow-sm"
                                                : "border-gray-50 bg-gray-50/30 text-gray-500 hover:border-gray-200 hover:bg-gray-100/50",
                                            errors.type && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-xl shrink-0 transition-colors",
                                            formData.type === type.id
                                                ? "bg-white shadow-sm text-blue-600"
                                                : cn(type.bg, type.color)
                                        )}>
                                            {type.icon}
                                        </div>
                                        <span className={cn(
                                            "font-bold text-sm tracking-tight",
                                            formData.type === type.id ? "text-blue-700" : "text-gray-700"
                                        )}>{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type-Specific Details (Custom Fields) */}
                        {customFields.length > 0 && (
                            <div className="pt-4 border-t border-gray-50 space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                                        <Box size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Type-Specific Details</h3>
                                        <p className="text-gray-500 text-xs font-medium">Additional information for {formData.type}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {customFields.map(field => (
                                        <div key={field.id} className="space-y-2">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">
                                                {field.fieldName} {field.isRequired && '*'}
                                            </label>

                                            {field.fieldType === 'text' && (
                                                <Input
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onChange={(e) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                    placeholder={`Enter ${field.fieldName}`}
                                                    className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}
                                                />
                                            )}

                                            {field.fieldType === 'number' && (
                                                <Input
                                                    type="number"
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onChange={(e) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                    placeholder={`0`}
                                                    className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}
                                                />
                                            )}

                                            {field.fieldType === 'select' && field.options && (
                                                <Select
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onValueChange={(val) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: val }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                >
                                                    <SelectTrigger className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}>
                                                        <SelectValue placeholder={`Select ${field.fieldName}`} />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-gray-100">
                                                        {field.options.map(opt => (
                                                            <SelectItem key={opt} value={opt} className="font-bold py-3">{opt}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}

                                             {field.fieldType === 'date' && (
                                                <div className="relative">
                                                    <Input
                                                        type="date"
                                                        max="9999-12-31"
                                                        value={customFieldValues[field.fieldName] || ''}
                                                        onChange={(e) => {
                                                            setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                            setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                        }}
                                                        className={cn(
                                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                                            errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                        )}
                                                    />
                                                </div>
                                            )}

                                            {field.fieldType === 'boolean' && (
                                                <Select
                                                    value={customFieldValues[field.fieldName] || 'No'}
                                                    onValueChange={(val) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: val }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                >
                                                    <SelectTrigger className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}>
                                                        <SelectValue placeholder="Select Yes/No" />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-gray-100">
                                                        <SelectItem value="Yes" className="font-bold py-3">Yes</SelectItem>
                                                        <SelectItem value="No" className="font-bold py-3">No</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Brand</label>
                                <Input
                                    name="brand"
                                    value={formData.brand}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Apple, Dell, Ford"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Model</label>
                                <Input
                                    name="model"
                                    value={formData.model}
                                    onChange={handleInputChange}
                                    placeholder="e.g., MacBook Pro, Transit"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Serial Number</label>
                                <Input
                                    name="serialNumber"
                                    value={formData.serialNumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter serial number"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Description</label>
                            <Textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Describe the asset and its purpose..."
                                className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[120px] focus:bg-white transition-all font-medium"
                            />
                        </div>
                    </section>

                    {/* Purchase & Warranty */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-green-50 rounded-xl text-green-600">
                                <DollarSign size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Purchase & Warranty</h3>
                                <p className="text-gray-500 text-sm font-medium">Financial and warranty information</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Purchase Date *</label>
                                <div className="relative">
                                     <Input
                                        type="date"
                                        name="purchaseDate"
                                        max="9999-12-31"
                                        value={formData.purchaseDate}
                                        onChange={handleInputChange}
                                        className={cn(
                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                            errors.purchaseDate && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Registration Date</label>
                                <div className="relative">
                                     <Input
                                        type="date"
                                        name="registrationDate"
                                        max="9999-12-31"
                                        value={formData.registrationDate}
                                        onChange={handleInputChange}
                                        className={cn(
                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                            errors.registrationDate && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    />
                                </div>
                            </div>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Purchase Price *</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                                        <Input
                                            name="purchasePrice"
                                            type="number"
                                            step="0.01"
                                            value={formData.purchasePrice}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            className={cn(
                                                "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-8",
                                                errors.purchasePrice && "border-red-500 ring-1 ring-red-500"
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Warranty Expiry Date *</label>
                                 <Input
                                    type="date"
                                    name="warrantyExpiryDate"
                                    max="9999-12-31"
                                    value={formData.warrantyExpiryDate}
                                    onChange={handleInputChange}
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.warrantyExpiryDate && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Inspection Cadence */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <CheckCircle2 size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Inspection Settings</h3>
                                <p className="text-gray-500 text-sm font-medium">Set the cadence for routine inspections</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Inspection Cadence Frequency</label>
                                <Input
                                    name="inspectionCadenceNumber"
                                    type="number"
                                    min="0"
                                    value={formData.inspectionCadenceNumber}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 90, 6, 12"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                <Select value={formData.inspectionCadenceUnit} onValueChange={(val) => handleSelectChange('inspectionCadenceUnit', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <SelectValue placeholder="Select Unit" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100">
                                        <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                        <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                        <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </section>

                    {/* Electrical Register */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-yellow-50 rounded-xl text-yellow-600">
                                <Info size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Electrical Equipment Register</h3>
                                <p className="text-gray-500 text-sm font-medium">Test & Tag compliance settings</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="space-y-0.5">
                                    <label className="text-sm font-bold text-gray-900 tracking-tight cursor-pointer" htmlFor="electrical-register">
                                        Add to Electrical Equipment Register
                                    </label>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Enable this for electrical tools/equipment that require routine Test & Tag.
                                    </p>
                                </div>
                                <Switch
                                    id="electrical-register"
                                    checked={formData.addToElectricalRegister}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addToElectricalRegister: checked }))}
                                />
                            </div>

                            {formData.addToElectricalRegister && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Per-Asset Cadence Override</label>
                                        <Input
                                            name="electricalCadenceNumber"
                                            type="number"
                                            min="1"
                                            value={formData.electricalCadenceNumber}
                                            onChange={handleInputChange}
                                            placeholder="Defaults to global setting"
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                        <p className="text-[10px] text-gray-400 font-medium ml-1">
                                            Leave blank to use the global default cadence.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                        <Select value={formData.electricalCadenceUnit} onValueChange={(val) => handleSelectChange('electricalCadenceUnit', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                                <SelectValue placeholder="Select Unit" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                                <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                                <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Lifting Register */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                <Anchor size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Lifting Equipment Register</h3>
                                <p className="text-gray-500 text-sm font-medium">Lifting gear and sling compliance settings</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="space-y-0.5">
                                    <label className="text-sm font-bold text-gray-900 tracking-tight cursor-pointer" htmlFor="lifting-register">
                                        Add to Lifting Equipment Register
                                    </label>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Enable this for lifting gear, slings, and shackles that require routine inspection.
                                    </p>
                                </div>
                                <Switch
                                    id="lifting-register"
                                    checked={formData.addToLiftingRegister}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addToLiftingRegister: checked }))}
                                />
                            </div>

                            {formData.addToLiftingRegister && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Per-Asset Cadence Override</label>
                                        <Input
                                            name="liftingCadenceNumber"
                                            type="number"
                                            min="1"
                                            value={formData.liftingCadenceNumber}
                                            onChange={handleInputChange}
                                            placeholder="Defaults to global setting"
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                        <p className="text-[10px] text-gray-400 font-medium ml-1">
                                            Leave blank to use the global default cadence.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                        <Select value={formData.liftingCadenceUnit} onValueChange={(val) => handleSelectChange('liftingCadenceUnit', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                                <SelectValue placeholder="Select Unit" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                                <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                                <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                <AlertCircle size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Status & Location</h3>
                                <p className="text-gray-500 text-sm font-medium">Current status and physical location</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Status</label>
                                <div className="rounded-2xl border border-gray-100 bg-gray-50 flex items-center h-12 px-4 cursor-not-allowed">
                                    <span className="font-bold text-gray-400">
                                        {formData.assignmentType === 'unassigned' ? 'Available (Ready for assignment)' : 'Assigned (Will be assigned immediately)'}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Condition</label>
                                <Select value={formData.condition} onValueChange={(val) => handleSelectChange('condition', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <SelectValue placeholder="Select Condition" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100">
                                        {CONDITION_OPTIONS.map(opt => (
                                            <SelectItem key={opt} value={opt} className="font-bold py-3">{opt}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                            <Input
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                placeholder="e.g., Warehouse A, Office Floor 2"
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                            />
                        </div>
                    </section>

                    {/* Initial Assignment */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
                                <User size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Initial Assignment (Optional)</h3>
                                <p className="text-gray-500 text-sm font-medium">Assign this asset to a user or department</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex flex-col gap-3">
                                {[
                                    { id: 'unassigned', label: 'Keep unassigned for now' },
                                    { id: 'user', label: 'Assign to specific user' },
                                    { id: 'department', label: 'Assign to department' }
                                ].map(option => (
                                    <label key={option.id} className="flex items-center gap-3 cursor-pointer group w-fit">
                                        <div
                                            onClick={() => setFormData(prev => ({ ...prev, assignmentType: option.id as any }))}
                                            className={cn(
                                                "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                                                formData.assignmentType === option.id
                                                    ? "border-slate-900 bg-slate-900"
                                                    : "border-gray-300 group-hover:border-gray-400 bg-white"
                                            )}
                                        >
                                            {formData.assignmentType === option.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                        </div>
                                        <span className={cn(
                                            "text-sm font-bold transition-colors",
                                            formData.assignmentType === option.id ? "text-gray-900" : "text-gray-600 group-hover:text-gray-900"
                                        )}>
                                            {option.label}
                                        </span>
                                    </label>
                                ))}
                            </div>

                            {formData.assignmentType === 'user' && (
                                <div className="pt-2 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Select User</label>
                                        <Select value={formData.assignedToUserId} onValueChange={(val) => handleSelectChange('assignedToUserId', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium text-gray-600">
                                                <SelectValue placeholder="Choose person..." />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                {users.map(u => (
                                                    <SelectItem key={u.id} value={u.id.toString()} className="font-medium py-3">{u.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Assignment Notes</label>
                                        <Input
                                            name="assignmentNotes"
                                            value={formData.assignmentNotes}
                                            onChange={handleInputChange}
                                            placeholder="Any notes about this assignment..."
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                    </div>
                                </div>
                            )}

                            {formData.assignmentType === 'department' && (
                                <div className="pt-2 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Select Department</label>
                                        <Select value={formData.assignedToDepartmentId} onValueChange={(val) => handleSelectChange('assignedToDepartmentId', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium text-gray-600">
                                                <SelectValue placeholder="Choose department..." />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                {departments.map(d => (
                                                    <SelectItem key={d.id} value={d.id.toString()} className="font-medium py-3">{d.departmentName}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Assignment Notes</label>
                                        <Input
                                            name="assignmentNotes"
                                            value={formData.assignmentNotes}
                                            onChange={handleInputChange}
                                            placeholder="Any notes about this assignment..."
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Conditional Signature Section */}
                            {formData.assignmentType !== 'unassigned' && (
                                <div className="space-y-3 pt-4 border-t border-gray-50 animate-in fade-in duration-300">
                                    <label className="text-xs font-bold text-gray-900">Digital Signature <span className="text-red-500">*</span></label>
                                    {signatureData ? (
                                        <div className="relative group p-4 rounded-2xl bg-gray-50/50 border border-gray-100 border-dashed flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                                                    <Check size={14} />
                                                </div>
                                                {signatureType === 'Draw' ? (
                                                    <img src={signatureData} alt="Signature" className="max-h-10" />
                                                ) : (
                                                    <span className="font-signature text-xl text-gray-800">{signatureData}</span>
                                                )}
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl px-3"
                                                onClick={() => setSignatureData('')}
                                            >
                                                Change
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full rounded-2xl border-dashed border-gray-200 h-20 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-blue-500 hover:border-blue-500 hover:bg-blue-50/30 transition-all"
                                            onClick={() => setIsSignatureDialogOpen(true)}
                                        >
                                            <Signature size={24} />
                                            <span className="text-xs font-bold uppercase tracking-widest">Capture Signature</span>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Asset Image */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-slate-50 rounded-xl text-slate-600">
                                <Upload size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Asset Image</h3>
                                <p className="text-gray-500 text-sm font-medium">Upload a photo of the asset (optional)</p>
                            </div>
                        </div>

                        {!imagePreview ? (
                            <div
                                className="border-2 border-dashed border-gray-100 rounded-[28px] p-12 text-center hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer group"
                                onClick={() => document.getElementById('image-upload')?.click()}
                            >
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                    <Upload size={24} className="text-gray-400 group-hover:text-blue-500" />
                                </div>
                                <p className="font-bold text-gray-900">Click to upload image</p>
                                <p className="text-gray-400 text-[10px] mt-1 uppercase tracking-widest font-bold">JPG, PNG or WEBP • MAX 5MB</p>
                                <input
                                    id="image-upload"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                />
                            </div>
                        ) : (
                            <div className="relative w-full aspect-video rounded-[28px] overflow-hidden border border-gray-100 shadow-inner group">
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                    <Button
                                        variant="destructive"
                                        className="rounded-xl h-10 px-4 font-bold gap-2"
                                        onClick={removeImage}
                                    >
                                        <Trash2 size={18} />
                                        Remove Photo
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        className="rounded-xl h-10 px-4 font-bold gap-2 bg-white text-gray-900 hover:bg-gray-50"
                                        onClick={() => document.getElementById('image-upload')?.click()}
                                    >
                                        <Upload size={18} />
                                        Replace
                                    </Button>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {/* Right Sidebar - Sticky Summary */}
                <div className="lg:col-span-1">
                    <div className="sticky top-24 space-y-6">
                        <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl shadow-gray-200/50 space-y-6">
                            <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                <LayoutGrid size={16} className="text-blue-500" />
                                Asset Summary
                            </h3>

                            <div className="space-y-4">
                                <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Name</span>
                                        <span className={cn("text-xs font-bold", formData.name ? "text-gray-900" : "text-gray-300")}>
                                            {formData.name || 'Not set'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Type</span>
                                        <span className={cn("text-xs font-bold", formData.type ? "text-blue-600" : "text-gray-300")}>
                                            {formData.type || 'Not selected'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                                        <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px] font-bold px-2 py-0.5 rounded-lg">
                                            {formData.status}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Assignment</span>
                                        <span className={cn(
                                            "text-xs font-bold",
                                            formData.assignmentType !== 'unassigned' ? "text-purple-600" : "text-gray-400"
                                        )}>
                                            {formData.assignmentType === 'unassigned' ? 'Unassigned' :
                                                formData.assignmentType === 'user' ? 'Direct to User' : 'To Department'}
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-3 pt-2">
                                    <Button
                                        className="w-full bg-slate-950 hover:bg-slate-900 text-white rounded-2xl h-14 font-bold shadow-lg shadow-slate-950/20 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        <ShieldCheck size={20} />
                                        {loading ? 'Processing...' : 'Create Asset'}
                                    </Button>

                                    <div className="space-y-2 px-1">
                                        <div className="flex items-start gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0 mt-0.5" />
                                            Asset will be added to inventory
                                        </div>
                                        <div className="flex items-start gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0 mt-0.5" />
                                            Activity will be logged
                                        </div>
                                        <div className="flex items-start gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0 mt-0.5" />
                                            Assignment notifications will be sent
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tips Card */}
                        <div className="bg-blue-600 p-6 rounded-[32px] text-white space-y-3 relative overflow-hidden group">
                            <TrendingUp className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10 group-hover:scale-110 transition-transform" />
                            <h4 className="font-bold flex items-center gap-2">
                                <Info size={16} />
                                Pro Tip
                            </h4>
                            <p className="text-xs font-medium leading-relaxed opacity-90">
                                You can assign assets directly during creation. This saves time and ensures immediate tracking from day one.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Signature Capture Modal */}
            <Dialog open={isSignatureDialogOpen} onOpenChange={setIsSignatureDialogOpen}>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Worker Signature</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant={signatureType === 'Type' ? 'default' : 'outline'}
                                onClick={() => setSignatureType('Type')}
                                className="flex-1 rounded-xl"
                            >
                                Type
                            </Button>
                            <Button
                                type="button"
                                variant={signatureType === 'Draw' ? 'default' : 'outline'}
                                onClick={() => setSignatureType('Draw')}
                                className="flex-1 rounded-xl"
                            >
                                Draw
                            </Button>
                        </div>

                        {signatureType === 'Type' ? (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700">Enter full name:</label>
                                <Input
                                    value={typedSignature}
                                    onChange={(e) => setTypedSignature(e.target.value)}
                                    placeholder="Your Full Name"
                                    className="font-signature text-lg rounded-xl h-12"
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-700">Draw signature:</label>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={150}
                                    className="w-full border rounded-2xl cursor-crosshair bg-gray-50"
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={stopDrawing}
                                />
                                <Button type="button" variant="outline" size="sm" onClick={clearCanvas} className="rounded-lg">
                                    Clear
                                </Button>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsSignatureDialogOpen(false)} className="rounded-xl">Cancel</Button>
                            <Button type="button" onClick={handleConfirmSignature} className="bg-black text-white rounded-xl">Confirm Signature</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CreateAsset;
